import Wrapper from './wrapper';
import { IWrapper } from './types';
import {
  IRuleset,
  IRulesetSchema,
  IRulesetListParams,
  IRulesetListResponse,
  IRulesetCreateParams,
  IRulesetUpdateParams,
  IRulesetDryRunRequest,
  IRulesetDryRunResponse,
} from '../types/ruleset-types';

class Ruleset {
  wrapper: IWrapper;

  constructor(wrapper: Wrapper) {
    this.wrapper = wrapper;
  }

  /**
   * Gets the schema for building rulesets (available fields, operators, actions)
   * @param dpid - Data pool ID
   */
  schema(dpid: string) {
    return this.wrapper.client.get<IRulesetSchema, IRulesetSchema>(
      `data-pools/${dpid}/rulesets/schema/document/`
    );
  }

  /**
   * Gets a paginated list of rulesets for a data pool
   * @param dpid - Data pool ID
   * @param params - Query parameters
   */
  list(dpid: string, params?: IRulesetListParams) {
    return this.wrapper.client.get<IRulesetListResponse, IRulesetListResponse>(
      `data-pools/${dpid}/rulesets/`,
      { params }
    );
  }

  /**
   * Gets a single ruleset by ID
   * @param dpid - Data pool ID
   * @param id - Ruleset ID
   */
  retrieve(dpid: string, id: string) {
    return this.wrapper.client.get<IRuleset, IRuleset>(
      `data-pools/${dpid}/rulesets/${id}/`
    );
  }

  /**
   * Creates a new ruleset
   * @param dpid - Data pool ID
   * @param data - Ruleset data
   */
  create(dpid: string, data: IRulesetCreateParams) {
    return this.wrapper.client.post<IRuleset, IRuleset>(
      `data-pools/${dpid}/rulesets/`,
      data
    );
  }

  /**
   * Updates an existing ruleset
   * @param dpid - Data pool ID
   * @param id - Ruleset ID
   * @param data - Partial ruleset data
   */
  update(dpid: string, id: string, data: IRulesetUpdateParams) {
    return this.wrapper.client.patch<IRuleset, IRuleset>(
      `data-pools/${dpid}/rulesets/${id}/`,
      data
    );
  }

  /**
   * Deletes a ruleset
   * @param dpid - Data pool ID
   * @param id - Ruleset ID
   */
  del(dpid: string, id: string) {
    return this.wrapper.client.delete(`data-pools/${dpid}/rulesets/${id}/`);
  }

  /**
   * Dry-runs a ruleset against a document and/or context override payload
   * @param dpid - Data pool ID
   * @param id - Ruleset ID
   * @param data - Dry-run request payload
   */
  dryRun(dpid: string, id: string, data: IRulesetDryRunRequest) {
    return this.wrapper.client.post<IRulesetDryRunResponse, IRulesetDryRunRequest>(
      `data-pools/${dpid}/rulesets/${id}/dry-run/`,
      data
    );
  }
}

export default Ruleset;
